import {BehaviorSubject, Observable, of, Subscription} from 'rxjs';
import {
  ApDynformsConfigFieldset
}                                                      from 'src/app/ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                      from 'src/app/ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                      from 'src/app/ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  IPpPlanningWizardDetailsData,
  IPpPlanningWizardDetailsFormData,
  IPpPlanningWizardDetailsResponsiveData,
  IPpPlanningWizardSelectionGridData,
  IPpProductListGridItem,
  IPpWizardProvider,
  PpConstants,
  PpWizardStates,
  SlopeLevel
}                                                      from '../pp-planning-wizard.types';
import {OperationModesEnum}                            from '../../../../stores/common/operation-modes.store';
import {
  GetRoundNumericService
}                                                      from '../../../../ap-utils/service/get-round-numeric.service';
import {SettingsStore}                                 from '../../../../stores/base-data/settings.store';
import {ApDateService}                                 from '../../../../ap-core/services/ap-date-service';
import {CampaignYearStore}                             from '../../../../stores/login/campaignyear.store';
import {nameOf}                                        from '../../../../ap-core/utils/ap-name-of';
import {ApDynformsComponent}                           from '../../../../ap-dynforms/ap-dynforms.component';
import {EventEmitter, Injectable, TemplateRef}         from '@angular/core';
import {
  ApDynGridPropertyColumnConfig
}                                                      from '../../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridButtonColumnConfig
}                                                      from '../../../../ap-dyngrids/config/ap-dyn-grid-button-column-config';
import {ApUtilService}                                 from '../../../../ap-utils/service/ap-util.service';
import {DetailsTargetRateUiProvider}                   from './ui-providers/details.target-rate.ui-provider';
import {DetailsUniformUiProvider}                      from './ui-providers/details.uniform.ui-provider';
import {
  PlantProtectionProductStore
}                                                      from '../../../../stores/base-data/plantprotection.product.store';
import {
  ApTranslationService
}                                                      from '../../../../ap-utils/service/ap-translation.service';
import {MachineStore}                                  from '../../../../stores/docu/machine.store';
import {Create, ListSortOrder, ObjectFactory}          from 'ts-tooling';
import {DetailsCommonUiProvider}                       from './ui-providers/details.common.ui-provider';
import {WorkTypeCategory, WorkTypes, WorkTypesStore}   from '../../../../stores/base-data/worrktypes.store';
import {CropTypeStore}                                 from '../../../../stores/base-data/crop.types.store';
import {GetRoundNumericPipe}                           from '../../../../ap-utils';
import {LoginStore}                                    from '../../../../stores/login/login.store';
import {
  ApDynGridIconColumnConfig
}                                                      from '../../../../ap-dyngrids/config/ap-dyn-grid-icon-column-config';
import {ApIconType}                                    from '../../../../icons/ap-icon-type';
import {
  DetailsTargetRateMapPerFieldUiProvider
}                                                      from './ui-providers/details.target-rate-map-per-field.ui-provider';
import {
  NFertilizationPlantProtectionService
}                                                      from '../../../../services/data/n-fertilization-plant-protection.service';
import {
  ApplicationSettingsService
}                                                      from '../../../../services/data/application-settings.service';
import {
  FieldScanData,
  GetGrowthStageData
}                                                      from '../../../../types/n-fertilization-plant-protection.types';
import {PpPlanningWizardSelectionService}              from '../services/pp-planning-wizard-selection.service';
import {
  DetailsTargetRateMapPerPlanUiProvider
}                                                      from './ui-providers/details.target-rate-map-per-plan.ui-provider';
import {
  ApColumnState
}                                                      from '../../../../ap-interface/enums/ap-column-state.enum';
import {ApplicationModesEnum}                          from '../../../../stores/common/appl-modes.store';
import {IOperationModeAppSetting}                      from '../../../../types/application-settings.types';
import {
  AgriportConstantsService
}                                                      from '../../../../services/common/agriport-constants.service';
import {
  AgriportConstantsEnum
}                                                      from '../../../../ap-interface/enums/ap-agriport-constants.enum';
import {InstrumentStore}                               from '../../../../stores/docu/instrument.store';
import IGuid = System.IGuid;
import IWorktypes = Data.BaseData.IWorktypes;
import IPlantProtectionProduct = Data.BaseData.IPlantProtectionProduct;
import IPlantProtectionProductDetails = Data.BaseData.IPlantProtectionProductDetails;
import IPlantProtectionProductListDetails = Data.TaskManagement.IPlantProtectionProductListDetails;
import IApplicationSettings = Data.Agronomic.IApplicationSettings;

// ATTENTION: injectable 'root' is very convenient in here because we can inject all needed stores.
// BUT: This will reuse THE SAME instance of the provider every time. Therefore, we need to carefully handle the 'onDestroy' method!
@Injectable({providedIn: 'root'})
export class PpPlanningWizardDetailsProvider implements IPpWizardProvider<IPpPlanningWizardSelectionGridData, IPpPlanningWizardDetailsData> {

  /**
   * More or less fixed data variables
   * (They are only set once on initialization)
   */
  private _eppoCodes: string[] = [];
  private _dynForm: ApDynformsComponent;
  private _productsTemplateRef: TemplateRef<any>;
  private _subscriptions: Subscription[] = [];
  private _validWorkTypes: IWorktypes[] = [];
  private _addProductCallback: EventEmitter<any>;
  private _selectedGridData: IPpPlanningWizardSelectionGridData[];
  private _operationModeAppSettings: IOperationModeAppSetting[];
  private _hideMinMaxColumns$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * These values might change during the lifetime of this wizard(step)
   */
  private _detailsData: IPpPlanningWizardDetailsData = undefined;
  private _caption$: BehaviorSubject<string>;
  private _fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>;
  /**
   * Variable to keep track of all responsive values.
   * These values might change due to user input (or later logic)
   */
  private _responsiveData: IPpPlanningWizardDetailsResponsiveData;

  /**
   * form properties which might trigger the ui to rebuild or other values to change
   */
  private _uiResponsiveProperties = [
    nameOf<IPpPlanningWizardDetailsFormData>('OperationModeKey'),
    nameOf<IPpPlanningWizardDetailsFormData>('WorkType')
  ];
  private _dataResponsiveProperties = [
    nameOf<IPpPlanningWizardDetailsFormData>('WorkType'),
    nameOf<IPpPlanningWizardDetailsFormData>('ECStage'),
    nameOf<IPpPlanningWizardDetailsFormData>('WaterAvg'),
    nameOf<IPpPlanningWizardDetailsFormData>('WaterRange'),
    nameOf<IPpPlanningWizardDetailsFormData>('PreProductId'),
    nameOf<IPpPlanningWizardDetailsFormData>('PreAvgAmount'),
    nameOf<IPpPlanningWizardDetailsFormData>('Machine'),
    nameOf<IPpPlanningWizardDetailsFormData>('Instrument')
  ];

  constructor(private settingsStore: SettingsStore,
              private dateService: ApDateService,
              private loginStore: LoginStore,
              private machineStore: MachineStore,
              private workTypesStore: WorkTypesStore,
              private cropTypesStore: CropTypeStore,
              private instrumentStore: InstrumentStore,
              private campaignYearStore: CampaignYearStore,
              private plantProtectionProductStore: PlantProtectionProductStore,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private agriportConstantsService: AgriportConstantsService,
              private applicationSettingsService: ApplicationSettingsService,
              private ppPlanningWizardSelectionService: PpPlanningWizardSelectionService,
              private nFertilizationPlantProtectionService: NFertilizationPlantProtectionService,
              private detailsCommonUiProvider: DetailsCommonUiProvider,
              private detailsUniformUiProvider: DetailsUniformUiProvider,
              private detailsTargetRateUiProvider: DetailsTargetRateUiProvider,
              private detailsTargetRateMapPerFieldUiProvider: DetailsTargetRateMapPerFieldUiProvider,
              private detailsTargetRateMapPerPlanUiProvider: DetailsTargetRateMapPerPlanUiProvider,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  /**
   * Initializes this wizard step's provider instance
   * @public
   */
  public init(caption$: BehaviorSubject<string>,
              fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>,
              dynForm: ApDynformsComponent,
              productsTemplateRef: TemplateRef<any>,
              productListItems$: BehaviorSubject<IPpProductListGridItem[]>): void {
    this._responsiveData = {
      PreselectedProductDetail$: new BehaviorSubject(undefined),
      AddProductButtonDisabled$: new BehaviorSubject<boolean>(true),
      PreselectValidProducts$: new BehaviorSubject<IPlantProtectionProduct[]>([]),
      PreselectedMaxProductRate$: new BehaviorSubject<number>(0),
      PreselectedMinProductRate$: new BehaviorSubject<number>(0),
      ProductListItems$: productListItems$,
      MaxWaterRange$: new BehaviorSubject<number>(PpConstants.MAX_WATER),
      MinWaterAvg$: new BehaviorSubject<number>(0)
    };
    this._operationModeAppSettings = [];
    this._addProductCallback = ApUtilService.createEventEmitter(this.addProduct.bind(this), this._subscriptions);
    this._caption$ = caption$;
    this._fieldSets$ = fieldSets$;
    this._dynForm = dynForm;
    this._productsTemplateRef = productsTemplateRef;
  }

  /**
   * Generates field statistics for the currently selected grid data
   * Calls the planning wizard selection service to create statistical information about the selected fields
   * @public
   */
  public onGenerateStatistic(): void {
    if (!this._selectedGridData) {
      return;
    }
    this.ppPlanningWizardSelectionService.generateFieldStatistic(this._selectedGridData, this._selectedGridData);
  }

  /**
   * Main method for handling user input.
   * Depending on the changed form values other values get changed or the ui is rebuilt
   * @param valueChangesArgs eventArgs from form's valueChanged event
   * @public
   */
  public onFormValueChanges(valueChangesArgs: { control: string; value: any }): void {
    /**
     * we need to update UI, Data, or Observables only after the user changed certain properties:
     */
    if (!this._uiResponsiveProperties.Exists(p => p === valueChangesArgs.control) &&
      !this._dataResponsiveProperties.Exists(p => p === valueChangesArgs.control)) {
      return;
    }

    /**
     * retrieve current values from the form
     * and modify its values if needed or rebuild UI if needed
     * also set operation mode app settings and control factor
     */
    this.updateDetailsData();

    if (!this._detailsData?.FormData) {
      return;
    }

    /**
     * User changes 'OperationMode'
     * => hiding min and max columns for product data in case if operation mode is Constant
     * => calculating nuptake average
     */
    if (valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('OperationModeKey')) {
      this._hideMinMaxColumns$.next(this._detailsData.FormData.OperationModeKey === OperationModesEnum.Constant);
      this._detailsData.FormData.NUptakeAverage = this.calculateNUptakeAreaWightedAverage(this._selectedGridData,
        this._detailsData.FormData.OperationModeKey);

      // Reset workType to 'spraying' if it's not a uniform planning
      if (this._detailsData.FormData.OperationModeKey !== OperationModesEnum.Constant) {
        this._detailsData.FormData.WorkType = WorkTypes.Spraying;
      }
    }

    /**
     * User changes 'OperationMode'
     * => define its operation key, which is used for comparison on different steps and form changes
     */
    if (valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('OperationModeKey') ||
      valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('WorkType')) {
      // set defaults if values are not defined, yet
      this._detailsData.FormData.WaterAvg ??= 200;
      this._detailsData.FormData.WaterRange ??= 100;
      this.updateWaterMinMaxValues(this._detailsData.FormData);
    }

    /**
     * User changes 'OperationMode' or 'EC-value', ...
     * => reset product data because this depends on operationMode and EC
     */
    if (valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('OperationModeKey') ||
      valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('ECStage') ||
      valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('WorkType') ||
      valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('WaterAvg') ||
      valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('WaterRange')) {
      this._responsiveData?.ProductListItems$.next([]);
      this._responsiveData?.PreselectedProductDetail$.next(undefined);

      this._detailsData.FormData.PreProductId = undefined;
      this._detailsData.FormData.PreAvgAmount = undefined;
      this._detailsData.FormData.PreMaxAmount = undefined;
      this._detailsData.FormData.PreMinAmount = undefined;

      // Only when changing control mode
      if (valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('OperationModeKey')
        && (this._detailsData?.FormData.OperationModeKey === OperationModesEnum.PpTargetMapPerField
          || this._detailsData?.FormData.OperationModeKey === OperationModesEnum.PPTargetMapPerPlan)
      ) {
        this.nFertilizationPlantProtectionService.onApplicationSettingsTextWarning(
          this._selectedGridData.map(x => ({FieldStatus: x.Status.state, ScanData: x.ScanData})),
          this._detailsData.OperationModeAppSetting.ApplicationSetting);
        if (!Number.isFinite(this._detailsData.FormData.ECStage)) {
          this._detailsData.FormData.ECStage = this.getMaxGrowthStageFromFields(this._selectedGridData,
            this._detailsData.OperationModeAppSetting.ApplicationSetting);
        }
      }

      this.updateValidProducts(this._detailsData.FormData);
      this.patchForm();
    }

    /**
     * User updates water values (applies for ps-target only)
     * => update min/max fields according to user input
     */
    if (valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('WaterAvg')
      || valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('WaterRange')) {
      if (this._detailsData?.FormData.OperationModeKey === OperationModesEnum.PpTarget
        || this._detailsData?.FormData.OperationModeKey === OperationModesEnum.PpTargetMapPerField
        || this._detailsData?.FormData.OperationModeKey === OperationModesEnum.PPTargetMapPerPlan) {
        this.updateWaterMinMaxValues(this._detailsData.FormData);
        this.patchForm();
      }
    }

    /**
     * User selected a product for adding it to the product selection grid
     * - reset the amount of input for the newly selected product
     * - update the max amount limit
     * - update the product avg/max labels
     */
    if (valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('PreProductId')) {
      this._responsiveData?.PreselectedMinProductRate$.next(0);
      this._responsiveData?.PreselectedMaxProductRate$.next(0);
      this._responsiveData?.PreselectedProductDetail$.next(this._responsiveData.PreselectValidProducts$.value
        .FirstOrDefault(p => p.Id === this._detailsData.FormData.PreProductId)?.ProductDetails?.FirstOrDefault());
      this._detailsData.FormData.PreAvgAmount = undefined;
      this._detailsData.FormData.PreMinAmount = this.calculateProductMinRate(this._detailsData.FormData,
        this._responsiveData?.PreselectedProductDetail$?.value);
      this._detailsData.FormData.PreMaxAmount = this.calculateProductMaxRate(this._detailsData.FormData,
        this._responsiveData?.PreselectedProductDetail$?.value);
      this._responsiveData?.PreselectedMaxProductRate$.next(this._detailsData.FormData.PreMaxAmount ?? 0);
      this._responsiveData?.PreselectedMinProductRate$.next(this._detailsData.FormData.PreMinAmount ?? 0);
      this.patchForm();
    }

    /**
     * User updates the used machine
     * => update driver/instrument with the default values if not set by the user already
     */
    if (valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('Machine')) {
      if (!!this._detailsData.FormData.Machine) {
        const machine = this.machineStore.getMachineById(this._detailsData.FormData.Machine);
        if (!this._detailsData.FormData.Driver) {
          this._detailsData.FormData.Driver = machine.DefaultDriver?.Id;
        }
        if (!this._detailsData.FormData.Instrument) {
          this.setInstrumentData(machine.DefaultInstrument?.Id, this._detailsData.FormData);
        }
        this.patchForm();
      }
    }

    /**
     * User updates the used machine
     * => update performance field with the default value (not null/undefined)
     */
    if (valueChangesArgs.control === nameOf<IPpPlanningWizardDetailsFormData>('Instrument')) {
      this.setInstrumentData(this._detailsData.FormData.Instrument, this._detailsData.FormData);
      this.patchForm();
    }

    /**
     * Update behavior subjects for the product template
     * - the 'add product to list' button according to a selected product and avg value
     * - preselected product's detail to update the product labels and max values
     */
    this._responsiveData?.AddProductButtonDisabled$.next(!(this._detailsData.FormData.PreProductId != null && this._detailsData.FormData.PreAvgAmount > 0)
      || this._responsiveData?.ProductListItems$?.value?.length >= PpConstants.MAX_SELECTABLE_PRODUCTS);

    /**
     * if the user did not change anything that affects the ui (rebuild fieldSets)
     * => we can exit here
     */
    if (!this._uiResponsiveProperties.Exists(p => p === valueChangesArgs.control)) {
      return;
    }

    /**
     * Update UI according to new values of the form
     * and patch the values again for the updated form
     */
    this.updateFieldSets();
    this.patchForm(true); // trigger validations after rebuilding UI
  }

  /**
   * Sets the instrument data for the provided details data object.
   * @param instrumentId - The ID of the instrument to be fetched and set.
   * @param detailsData - The details data object where the instrument information will be set.
   * @return This method does not return a value.
   * @private
   */
  private setInstrumentData(instrumentId: IGuid | undefined, detailsData: IPpPlanningWizardDetailsFormData): void {
    const instrument = this.instrumentStore.getInstrumentById(instrumentId);
    detailsData.Instrument = instrument?.Id;
    if (Number.isFinite(instrument?.DefaultHaPerHour)) {
      detailsData.HaPerHour = instrument.DefaultHaPerHour;
    }
  }

  /**
   * Updates minimum and maximum water values based on average and range
   * Updates related BehaviorSubjects for UI constraints
   * @param formData Current form data object
   * @private
   */
  private updateWaterMinMaxValues(formData: IPpPlanningWizardDetailsFormData): void {
    formData.WaterMin = formData.WaterAvg <= 0 ? undefined : +formData.WaterAvg - +(formData.WaterRange ?? 0);
    formData.WaterMax = formData.WaterAvg <= 0 ? undefined : +formData.WaterAvg + +(formData.WaterRange ?? 0);
    this._responsiveData?.MaxWaterRange$.next(this.calculateMaxWorkingRange(formData.WaterAvg));
    this._responsiveData?.MinWaterAvg$.next(Math.max(formData.WaterRange ?? 0, 0));
  }

  /**
   * Method applies the current data transfer object (formDto) to the reactive form
   * without emitting new events to avoid triggering onFormValueChanges again.
   * @param triggerValidation true to trigger validations for the whole form
   * @private
   */
  private patchForm(triggerValidation?: boolean): void {
    setTimeout(() => {
      this._dynForm.form.patchValue(this._detailsData.FormData, {emitEvent: false});
      if (triggerValidation) {
        setTimeout(() => this._dynForm.markAsTouched(), 0);
      }
    }, 0);
  }

  /**
   * Builds the INITIAL details form data object which is used to patch values to the fieldSets
   * @param data the items which were selected by user in a selection wizard step
   * @public
   */
  public buildItem(data: IPpPlanningWizardSelectionGridData[]): IPpPlanningWizardDetailsData {
    this._selectedGridData = data;
    this._operationModeAppSettings = this.getOperationModeAppSettings(data);
    this._validWorkTypes = this.getValidWorkTypes();
    this.ppPlanningWizardSelectionService.generateFieldStatistic(data, data);
    const endOfCampaignYear = this.dateService.toJsDate(
      this.dateService.toFarmDateFromUtc(this.campaignYearStore.getSelectedCampaignYear().DefaultEnd)
    );
    const cropName = data?.FirstOrDefault()?.Crop ?? '-';
    const cropType = this.cropTypesStore.getDefaultCroptype(data?.FirstOrDefault()?.CropId);
    // update current eppoCodes defined by the crop of the selected fields
    //  - new planning allows fields with the same crops only
    //  - crop has a main eppoCode
    //  - crop may be part of hierarchical eppo groups (e.g., Winter Wheat is part of the group of (general) Wheat)
    this._eppoCodes = [];
    if (cropType != null) {
      if (cropType.Eppocode?.length > 0) {
        this._eppoCodes.push(cropType.Eppocode);
      }
      if (cropType.EppoGroups?.length > 0) {
        this._eppoCodes.AddRange(cropType.EppoGroups.filter(e => e?.length > 0));
      }
    }
    // In case the user changed the crop while navigating back to the selection grid,
    // we need to reset the product selection
    if (this._detailsData?.FormData.Crop !== cropName) {
      this._responsiveData?.ProductListItems$?.next([]);
      this._responsiveData?.PreselectValidProducts$.next([]);
      if (this._detailsData?.FormData.ECStage > 0) {
        this._detailsData.FormData.ECStage = undefined;
      }
    }
    const totalArea = this.roundNumericService.round(
      data.reduce((sum, s) => sum + Create(s?.Area, 0), 0),
      this.settingsStore.FirstSetting.DigitsAfterDecimalPoint
    );
    const selectedOperationSetting = this.getOperationModeAppSettingsOrDefault(
      this._detailsData?.FormData?.OperationModeKey);
    const nUptakeAverage = this.calculateNUptakeAreaWightedAverage(data, selectedOperationSetting?.OperationMode);
    // to apply changes to already existing formData,
    // the user navigated back/forward through the wizard, and we
    // want to keep the previous values as much as possible
    if (!!this._detailsData) {
      this._detailsData.FormData.TotalArea = totalArea;
      this._detailsData.FormData.CountFields = data?.length;
      this._detailsData.FormData.Crop = cropName; // new pp planning only allowed for ONE distinct crop
      this._detailsData.FormData.OperationModeKey = selectedOperationSetting?.OperationMode;
      this._detailsData.FormData.NUptakeAverage = nUptakeAverage;
      this._detailsData.FormData.PreProductId = undefined;
      this._detailsData.ControlFactor = this.getControlFactor(selectedOperationSetting?.ApplicationSetting);
      this._detailsData.OperationModeAppSetting = selectedOperationSetting;
    } else {
      this._detailsData = {
        FormData: {
          TotalArea: totalArea,
          CountFields: data?.length,
          Crop: cropName, // new pp planning only allowed for ONE distinct crop
          OperationModeKey: selectedOperationSetting?.OperationMode,
          WorkType: WorkTypes.Spraying,
          ApplicationDate: new Date(Math.min(new Date().valueOf(), endOfCampaignYear.valueOf())),
          NUptakeAverage: nUptakeAverage,
          ECStage: this.getMaxGrowthStageFromFields(data, selectedOperationSetting?.ApplicationSetting),
          Note: '',
          Threshold: undefined,
          WaterAvg: undefined,
          WaterMax: undefined,
          WaterMin: undefined,
          WaterRange: undefined,
          WaterSetZeroAtMin: false,
          PreProductId: undefined,
          PreAvgAmount: undefined,
          PreMaxAmount: undefined,
          PreMinAmount: undefined,
          Instrument: undefined,
          Driver: undefined,
          Machine: undefined,
          HaPerHour: undefined
        },
        ControlFactor: this.getControlFactor(selectedOperationSetting?.ApplicationSetting),
        OperationModeAppSetting: selectedOperationSetting
      };
    }
    return this._detailsData;
  }

  /**
   * Creates columns for the product grid (on a details form)
   * @public
   */
  public getProductListColumns(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPpProductListGridItem>('ProductName'),
        style: {fontWeight: '600'},
        tooltip: (dataItem: IPpProductListGridItem) => this.generateProductNameTooltip(dataItem)
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPpProductListGridItem>('Min'),
        width: 60,
        cssClass: 'right',
        style: {fontSize: '12px'},
        hide: new ApDynGridColumnHideConfig({
          selfHide: this._hideMinMaxColumns$
        }),
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [this.settingsStore.FirstSetting?.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPpProductListGridItem>('ApplRate'),
        width: 60,
        cssClass: 'right',
        style: {fontWeight: '600'},
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [this.settingsStore.FirstSetting?.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPpProductListGridItem>('Max'),
        width: 60,
        cssClass: 'right',
        style: {fontSize: '12px'},
        hide: new ApDynGridColumnHideConfig({
          selfHide: this._hideMinMaxColumns$
        }),
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [this.settingsStore.FirstSetting?.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPpProductListGridItem>('UnitExpr'),
        width: 60
      }),
      new ApDynGridIconColumnConfig({
        cssClass: 'center',
        tooltip: (dataItem: IPpProductListGridItem) => dataItem?.GrowthStageDefined === false ? 'NO_GROWTH_STAGE_DEFINED' : '',
        iconClass: () => 'ap-icon-medium',
        icon: (dataItem: IPpProductListGridItem) => dataItem?.GrowthStageDefined === false ? ApIconType.ApInfoIconRound : undefined,
        width: 40
      }),
      new ApDynGridButtonColumnConfig(
        {
          tooltip: 'Global__Delete',
          callback: ApUtilService.createEventEmitter(this.deleteProduct.bind(this), this._subscriptions),
          isStandardIcon: true,
          standardIcon: nameOf<IPpProductListGridItem>('ButtonIconName')
        }
      )];
  }

  /**
   * Whenever conditions changes (Ec-value, worktype, etc...),
   * the product selection needs an update. This method updates the source
   * of the corresponding comboBox
   * @private
   */
  private updateValidProducts(formData: IPpPlanningWizardDetailsFormData): void {
    /**
     * basic condition to show products
     */
    if (formData.ECStage == null || formData.ECStage < 0) {
      this._responsiveData?.PreselectValidProducts$.next([]);
      return;
    }

    /**
     * if the workType is spraying,
     * the water field is mandatory to be able to choose a product
     */
    if (formData.WorkType === WorkTypes.Spraying
      && (this._detailsData.FormData.WaterAvg == null || formData.WaterAvg <= 0)) {
      this._responsiveData?.PreselectValidProducts$.next([]);
      return;
    }

    /**
     * PS-target product conditions:
     * - workType: spray (because it's mixed with water)
     * - all water fields (avg and range) are mandatory
     * - matching the field's crop (field eppoCode == product eppoCode)
     * - matching EC value
     */
    if ((formData.OperationModeKey === OperationModesEnum.PpTarget
        || formData.OperationModeKey === OperationModesEnum.PpTargetMapPerField
        || formData.OperationModeKey === OperationModesEnum.PPTargetMapPerPlan) &&
      (formData.WaterRange == null || formData.WaterRange <= 0)) {
      this._responsiveData?.PreselectValidProducts$.next([]);
      return;
    }

    /**
     * Find the matching products
     */
    const selectableProducts = this.getSelectableProducts();
    const validProducts: IPlantProtectionProduct[] = [];
    for (const ppProduct of selectableProducts) {
      const validDetails = ppProduct.ProductDetails
        .filter(d => formData.ECStage >= d.EcMin &&
          formData.ECStage <= d.EcMax &&
          this._eppoCodes?.Contains(d.EppoCrop) &&
          d.WorkType?.Id === formData.WorkType &&
          !this._responsiveData?.ProductListItems$?.value?.Any(p =>
            p.ProductListItem?.Product?.Id === d.ProductId)
        );
      if (validDetails?.length <= 0) {
        continue;
      }

      // here we create a copy of the original product to
      // be able to have an ONLY ONE product detail which actually applies for this planning
      const validProduct = ObjectFactory.Copy(ppProduct);
      validProduct.ProductDetails = [this.findMaxProductDetail(validDetails)];
      validProducts.push(validProduct);
    }
    this._responsiveData?.PreselectValidProducts$.next(validProducts?.SortBy([nameOf<IPlantProtectionProduct>('ProductName')], [ListSortOrder.ASC]));
  }

  /**
   * Products which are selectable (without any restriction on eppoCodes, ...)
   * These products are valid in the matter of:
   * - Selectable
   * - not deleted
   * - product's permission is still valid
   * @private
   */
  private getSelectableProducts(): IPlantProtectionProduct[] {
    return this.plantProtectionProductStore.Listen(p => p.data).getValue()
      .filter(p => p.Selectable && p.DeletedAt == null &&
        new Date(p.UseByDate) >= this.dateService.getDateMidnight(new Date()));
  }

  /**
   * Adds a new product to the product selection grid based on the currently selected product and amount
   * Validates the product can be added, creates a grid item with appropriate properties,
   * updates the product list, and resets the form fields for the next product selection
   * @public
   */
  public addProduct(): void {
    this.updateDetailsData();
    const selectedProducts = this._responsiveData?.ProductListItems$.getValue();
    const productToAdd = this._responsiveData?.PreselectValidProducts$.getValue()
      .FirstOrDefault(p => p.Id === this._detailsData.FormData.PreProductId);
    const avgAmount = +this._detailsData.FormData.PreAvgAmount;
    const maxAmount = +this._detailsData.FormData.PreMaxAmount;

    const minWater = +this._detailsData.FormData.WaterMin;
    const maxWater = +this._detailsData.FormData.WaterMax;
    const avgWater = +this._detailsData.FormData.WaterAvg;

    if (!productToAdd || selectedProducts.Any(p => p?.ProductListItem?.Product?.Id === productToAdd.Id) ||
      avgAmount <= 0 || maxAmount <= 0) {
      return;
    }

    // We need to map from product's details to product list details
    // It might seem weird, but we need to save the current product details as
    // a simplified version attached to the created planBook.
    // Reason: The original product details might change over time
    // by re-importing BVL product data (external product source from the government)
    const productDetail = productToAdd?.ProductDetails?.FirstOrDefault();
    const productListDetail: IPlantProtectionProductListDetails = {
      Id: productDetail?.Id,
      AmountMax: productDetail?.AmountMax,
      WorkType: productDetail?.WorkType?.Id,
      EcMax: productDetail?.EcMax,
      EcMin: productDetail?.EcMin,
      EppoCrop: productDetail?.EppoCrop,
      EffectiveRange: productDetail?.EffectiveRange?.Id,
      EppoHarmfulOrganism: productDetail?.EppoHarmfulOrganism
    };
    const productToAddGridItem: IPpProductListGridItem = {
      ProductListItem: {
        ProductListDetails: productListDetail,
        Product: productToAdd,
        ProductRate: avgAmount,
        FarmId: this.loginStore.SelectedFarmId,
        PlanId: undefined,
        Id: undefined,
        Unit: productDetail?.Unit
      },
      ProductName: this.generateProductName(productToAdd, productDetail),
      Min: Number.isFinite(avgWater) ? minWater * avgAmount / avgWater : undefined,
      Max: Number.isFinite(avgWater) ? maxWater * avgAmount / avgWater : undefined,
      ApplRate: avgAmount,
      UnitExpr: this.translationService.translate(productDetail?.Unit?.Key),
      GrowthStageDefined: productDetail?.GrowthStageDefined,
      ButtonIconName: 'trashIcon'
    };

    this._responsiveData?.ProductListItems$.next([...selectedProducts, ...[productToAddGridItem]]);

    this._detailsData.FormData.PreProductId = undefined;
    this._detailsData.FormData.PreAvgAmount = undefined;
    this._detailsData.FormData.PreMaxAmount = undefined;
    this._detailsData.FormData.PreMinAmount = undefined;
    this.patchForm();
    this._responsiveData?.PreselectedProductDetail$.next(undefined);
    this._responsiveData?.AddProductButtonDisabled$.next(true);
    this.updateValidProducts(this._detailsData.FormData);
  }

  /**
   * Removes a product from the grid when the delete button is clicked
   * Updates the grid data source and refreshes the valid products list
   * @param dataItem The product item to be deleted from the grid
   * @public
   */
  public deleteProduct(dataItem: IPpProductListGridItem): void {
    this.updateDetailsData();
    const selectedProducts = this._responsiveData?.ProductListItems$.getValue();
    this._responsiveData?.ProductListItems$.next(selectedProducts.filter(p => p.ProductListItem?.Product?.Id !== dataItem?.ProductListItem?.Product?.Id));
    this.updateValidProducts(this._detailsData.FormData);
  }

  /**
   * Finds the most suitable detail of the product
   * In general, there is only one product detail for an eppo-code, EC and worktype
   * combination => but in case of BVL data inconsistencies, we take the details with the largest amount
   * @param productDetails
   * @private
   */
  private findMaxProductDetail(productDetails: IPlantProtectionProductDetails[]): IPlantProtectionProductDetails {
    if (!productDetails || productDetails?.length <= 0) {
      return undefined;
    }
    let maxDetail: IPlantProtectionProductDetails;
    for (const productDetail of productDetails) {
      if (!maxDetail || maxDetail.AmountMax < productDetail?.AmountMax) {
        maxDetail = productDetail;
      }
    }
    return maxDetail;
  }

  /**
   * Calculates the maximum rate of a product based on the current operation mode
   * For constant mode: returns the product's maximum amount
   * For target modes: calculates based on water average and maximum values
   * @param formData Current form data containing operation mode and water parameters
   * @param preselectedProductDetails Details of the selected product
   * @returns The calculated maximum rate or undefined if conditions aren't met
   * @private
   */
  private calculateProductMaxRate(formData: IPpPlanningWizardDetailsFormData,
                                  preselectedProductDetails: IPlantProtectionProductDetails | undefined): number | undefined {
    // no product selected
    if (!preselectedProductDetails) {
      return undefined;
    }
    const maxAmount = +preselectedProductDetails.AmountMax;
    switch (formData.OperationModeKey) {
      case OperationModesEnum.Constant:
        return maxAmount;
      case OperationModesEnum.PpTarget:
      case OperationModesEnum.PpTargetMapPerField:
      case OperationModesEnum.PPTargetMapPerPlan:
        if (!Number.isFinite(formData.WaterAvg) || formData.WaterAvg <= 0
          || !Number.isFinite(formData.WaterMax) || formData.WaterMax <= 0) {
          return undefined;
        }
        const calculationResult = maxAmount * +formData.WaterAvg / +formData.WaterMax;
        return this.roundNumericService.roundAsNumber(calculationResult, 3);
      default:
        return undefined;
    }
  }

  /**
   * Calculates the minimum rate of a product based on the current form data
   * Uses a predefined constant to determine the minimum product rate relative to the maximum rate
   * Calculation takes into account water average and minimum values
   * @param formData Current form data containing water and product parameters
   * @param preselectedProductDetails
   * @returns The calculated minimum rate or undefined if conditions aren't met
   * @private
   */
  private calculateProductMinRate(formData: IPpPlanningWizardDetailsFormData,
                                  preselectedProductDetails: IPlantProtectionProductDetails | undefined): number | undefined {
    // no product selected
    if (!preselectedProductDetails) {
      return undefined;
    }
    const minProductOfProductMax = +this.agriportConstantsService.GetConstant(AgriportConstantsEnum.PpMinProductOfProductMax);
    const maxAmount = +preselectedProductDetails.AmountMax;
    switch (formData.OperationModeKey) {
      case OperationModesEnum.Constant:
        return maxAmount * minProductOfProductMax;
      case OperationModesEnum.PpTarget:
      case OperationModesEnum.PpTargetMapPerField:
      case OperationModesEnum.PPTargetMapPerPlan:
        if (!Number.isFinite(formData.WaterAvg) || formData.WaterAvg <= 0
          || !Number.isFinite(formData.WaterMin) || formData.WaterMin <= 0
          || formData.WaterAvg < formData.WaterMin) {
          return undefined;
        }
        const calculationResult = maxAmount * minProductOfProductMax * +formData.WaterAvg / +formData.WaterMin;
        return this.roundNumericService.roundAsNumber(calculationResult, 3);
      default:
        return undefined;
    }
  }

  /**
   * Calculates the maximum allowed working range for the given WaterAvg.   *
   * If the working range is too large, the product's MinRate can exceed the MaxRate.   *
   * This happens when:
   *   minProductOfProductMax * WaterMax > WaterMin   *
   * @param waterAvg - The average water amount entered by the user
   * @returns The maximum allowed working range
   */
  private calculateMaxWorkingRange(waterAvg: number): number {
    const minProductOfProductMax = +this.agriportConstantsService
      .GetConstant(AgriportConstantsEnum.PpMinProductOfProductMax);

    return Math.floor(waterAvg * (1 - minProductOfProductMax) / (1 + minProductOfProductMax));
  }

  /**
   * Leaving the details wizard step.
   * Extract the current form's value to be able to restore them once the user comes back.
   * Returns a copy of a form data object.
   * @public
   */
  public onLeave(): IPpPlanningWizardDetailsData {
    this._selectedGridData = undefined;
    this.ppPlanningWizardSelectionService.generateFieldStatistic([], []);
    this.updateDetailsData();
    return ObjectFactory.Copy(this._detailsData);
  }

  /**
   * Finds all worktypes with the following conditions:
   *  - it is a plant protection worktype
   *  - its 'spray' worktype (we always need this, because it's the default)
   *  - or it's a worktype of the selectable products of the farm
   * @private
   */
  private getValidWorkTypes(): IWorktypes[] {
    const selectedWorkTypeIdsByProduct = this.getSelectableProducts()
      .map(p => p.ProductDetails)
      .flat().map(d => d.WorkType?.Id);

    return this.workTypesStore.Listen(s => s.data).getValue()
      .filter(w => w.Category === WorkTypeCategory.PlantProtection &&
        (w.Id === WorkTypes.Spraying || selectedWorkTypeIdsByProduct.Contains(w.Id)));
  }

  /**
   * Creates a display name for a product by combining product name and effective range
   * @param product The plant protection product
   * @param productDetail The specific detail of the product
   * @returns Formatted product name string
   * @private
   */
  private generateProductName(product: IPlantProtectionProduct,
                              productDetail: IPlantProtectionProductDetails | undefined): string {
    const productName = this.translationService.translate(product.ProductName);
    if (!!productDetail?.EffectiveRange?.Key) {
      const effectiveRange = this.translationService.translate(productDetail?.EffectiveRange?.Key);
      return `${effectiveRange} | ${productName}`;
    }
    return productName;
  }

  /**
   * Updates the UI caption when the user navigates to the details step
   * Sets the caption to a generic "Details" label
   * @public
   */
  public updateCaption(): void {
    this._caption$.next('Global__Details');
  }

  /**
   * Rebuilds the form field sets based on the current operation mode
   * Adds common fields first, then specific fields based on the operation mode
   * Includes product selection and documentation fields when an operation mode is selected
   * @public
   */
  public updateFieldSets(): void {
    const generatedFieldSets = this.detailsCommonUiProvider.createGeneralFieldset(
      this._detailsData?.FormData?.OperationModeKey, this._operationModeAppSettings, this._validWorkTypes);
    switch (this._detailsData?.FormData?.OperationModeKey) {
      case OperationModesEnum.Constant:
        generatedFieldSets.AddRange(this.detailsUniformUiProvider.createUniformFieldset(this._detailsData?.FormData));
        break;
      case OperationModesEnum.PpTarget:
        generatedFieldSets.AddRange(this.detailsTargetRateUiProvider.createTargetRateFieldset(this._responsiveData));
        break;
      case OperationModesEnum.PpTargetMapPerField:
        generatedFieldSets.AddRange(this.detailsTargetRateMapPerFieldUiProvider.createTargetRateMapPerFieldFieldset(
          this._responsiveData));
        break;
      case OperationModesEnum.PPTargetMapPerPlan:
        generatedFieldSets.AddRange(this.detailsTargetRateMapPerPlanUiProvider.createTargetRateMapPerPlanFieldset(
          this._responsiveData));
        break;
    }
    if (this._detailsData?.FormData?.OperationModeKey?.length > 0) {
      generatedFieldSets.AddRange(this.detailsCommonUiProvider.createProductsFieldset(this._productsTemplateRef,
        this._responsiveData, this._addProductCallback));
      generatedFieldSets.Add(this.detailsCommonUiProvider.createDocuFieldSet(this._detailsData?.FormData));
    }
    this._fieldSets$.next(generatedFieldSets);
  }

  /**
   * Handles cleanup when the component is destroyed
   * Unsubscribes from all subscriptions and resets data variables
   * @public
   */
  public onDestroy(): void {
    this._responsiveData = undefined;
    this._detailsData = undefined;
    this._selectedGridData = undefined;
    this._subscriptions?.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  /**
   * Handler for grid selection changes
   * Implementation of interface method (currently empty)
   * @param _items Selected grid items
   * @public
   */
  public onGridSelectionChanged(_items: IPpPlanningWizardSelectionGridData[]): void {
    // only for interface => nothing to do here
  }

  /**
   * Returns grid column configuration
   * Implementation of interface method
   * @returns Observable with an empty array (no columns defined in this provider)
   * @public
   */
  public getGridColumnConfig(): Observable<ApDynGridColumnConfigBase[]> {
    return of([]);
  }

  /**
   * Returns pager configuration for the grid
   * Implementation of interface method
   * @param _breadCrumbsColumns Array of wizard states for breadcrumbs
   * @returns Undefined as no pager configuration is needed
   * @public
   */
  public getPagerConfig(_breadCrumbsColumns: PpWizardStates[]): ApDynGridPagerConfigBase {
    return undefined;
  }

  /**
   * Updates the details data object with current form values
   * Sets the form data from raw form values, updates operation mode settings,
   * and recalculates a control factor based on current application settings
   * @private
   */
  private updateDetailsData(): void {
    this._detailsData.FormData = this._dynForm.form.getRawValue() as IPpPlanningWizardDetailsFormData;
    this._detailsData.OperationModeAppSetting = this._operationModeAppSettings.find(x => x.OperationMode === this._detailsData.FormData.OperationModeKey);
    this._detailsData.ControlFactor = this.getControlFactor(this._detailsData.OperationModeAppSetting?.ApplicationSetting);
  }

  /**
   * Determines the control factor based on provided application settings
   * For numeric slope types, returns the first slope value
   * For dropdown slope types, returns the medium slope level or falls back to first slope value
   * @param applicationSettings The application settings containing slope parameters
   * @returns The calculated control factor or default (-1) if no valid slope type is found
   * @private
   */
  private getControlFactor(applicationSettings: IApplicationSettings | undefined): number {
    const defaultSlope = -1;
    switch (applicationSettings?.Parameter?.slopeType) {
      case 'numeric':
        return applicationSettings.Parameter.slope[0]?.Slope ?? defaultSlope;
      case 'dropDown':
        return applicationSettings.Parameter.slope?.find(x => x.Key === SlopeLevel.Medium)?.Slope
          ?? applicationSettings.Parameter.slope[0]?.Slope
          ?? defaultSlope;
      default:
        return defaultSlope;
    }
  }

  /**
   * Gets available operation mode settings based on field status
   * Different operation modes require fields with specific statuses
   * Adds special application settings for Agricon roles when scan data is available
   * @param data Selected field data
   * @returns Array of available operation mode application settings
   * @private
   */
  private getOperationModeAppSettings(data: IPpPlanningWizardSelectionGridData[]): IOperationModeAppSetting[] {
    if (!data) {
      return undefined;
    }
    const operationModeAppSettings: IOperationModeAppSetting[] = [];
    if (data.every(s => s.Status.state >= ApColumnState.Yellow)) {
      operationModeAppSettings.push({
        Id: OperationModesEnum.Constant,
        OperationMode: OperationModesEnum.Constant,
        ApplicationMode: ApplicationModesEnum.Constant,
        Text: OperationModesEnum.Constant,
        Category: 'OperationMode_Group_Task',
        ApplicationSetting: undefined
      });
    }
    if (data.TrueForAll(s => s.Status.state >= ApColumnState.Green)) {
      const eppoCode = this.ppPlanningWizardSelectionService.getEppoCodeFromSelectedFields(data);
      const ppTargetApplicationSettings = this.applicationSettingsService.getApplicationSetting(
        OperationModesEnum.PpTarget, eppoCode);
      operationModeAppSettings.push({
        Id: OperationModesEnum.PpTarget,
        Text: OperationModesEnum.PpTarget,
        OperationMode: OperationModesEnum.PpTarget,
        ApplicationMode: ApplicationModesEnum.Variable,
        Category: 'OperationMode_Group_Task',
        ApplicationSetting: ppTargetApplicationSettings
      });
    }
    const fieldsWithValidScanData = data.filter(x => this.nFertilizationPlantProtectionService.isValidScanData(x.ScanData));
    if (fieldsWithValidScanData.length > 0) {
      const eppoCode = this.ppPlanningWizardSelectionService.getEppoCodeFromSelectedFields(data);
      const ppTargetMapPerFieldApplicationSettings = this.applicationSettingsService.getApplicationSetting(
        OperationModesEnum.PpTargetMapPerField, eppoCode);
      if (!!ppTargetMapPerFieldApplicationSettings) {
        operationModeAppSettings.push({
          Id: OperationModesEnum.PpTargetMapPerField,
          Text: OperationModesEnum.PpTargetMapPerField,
          OperationMode: OperationModesEnum.PpTargetMapPerField,
          ApplicationMode: ApplicationModesEnum.Variable,
          Category: 'OperationMode_Group_Map',
          ApplicationSetting: ppTargetMapPerFieldApplicationSettings
        });
      }
      const ppTargetMapPerAvgMapApplicationSettings = this.applicationSettingsService.getApplicationSetting(
        OperationModesEnum.PPTargetMapPerPlan, eppoCode);
      if (!!ppTargetMapPerAvgMapApplicationSettings) {
        operationModeAppSettings.push({
          Id: OperationModesEnum.PPTargetMapPerPlan,
          Text: OperationModesEnum.PPTargetMapPerPlan,
          OperationMode: OperationModesEnum.PPTargetMapPerPlan,
          ApplicationMode: ApplicationModesEnum.Variable,
          Category: 'OperationMode_Group_Map',
          ApplicationSetting: ppTargetMapPerAvgMapApplicationSettings
        });
      }
    }
    return operationModeAppSettings;
  }

  /**
   * Gets the operation mode setting by key or returns a default if not found
   * When only one operation mode is available, returns that one
   * @param operationModeEnum The operation mode key to search for
   * @returns Matching operation mode setting or default
   * @private
   */
  private getOperationModeAppSettingsOrDefault(operationModeEnum: OperationModesEnum | undefined): IOperationModeAppSetting | undefined {
    let existingOperationModeAppSetting: IOperationModeAppSetting | undefined;
    if (!!operationModeEnum) {
      existingOperationModeAppSetting = this._operationModeAppSettings.find(x => x.OperationMode === this._detailsData?.FormData?.OperationModeKey);
    }
    return this._operationModeAppSettings?.length === 1
      ? this._operationModeAppSettings[0]
      : existingOperationModeAppSetting;
  }

  /**
   * Determines the maximum growth stage across all selected fields
   * Uses scan data and application settings to calculate appropriate growth stage
   * @param data Selected field data containing scan information
   * @param applicationSettings Application settings to use for validation
   * @returns The maximum growth stage value or undefined if not determinable
   * @private
   */
  private getMaxGrowthStageFromFields(data: IPpPlanningWizardSelectionGridData[],
                                      applicationSettings: IApplicationSettings): number | undefined {
    if (!applicationSettings) {
      return undefined;
    }
    const scanData = data.filter(x => this.nFertilizationPlantProtectionService.isValidScanData(x.ScanData))
      .map(x => x.ScanData);
    if (!scanData || scanData.length === 0) {
      return undefined;
    }
    const fieldsScanData = data.map(x => ({FieldStatus: x.Status.state, ScanData: x.ScanData} as FieldScanData));
    const scansByApplicationSettings = this.nFertilizationPlantProtectionService.getValidBestScansByApplicationSettings(
      fieldsScanData, applicationSettings);
    const growthStages = this.nFertilizationPlantProtectionService.getGrowStages(
      scansByApplicationSettings.map(x => ({ScanData: x} as GetGrowthStageData))
    );
    return growthStages.Max();
  }

  /**
   * Calculates the area-weighted average N-uptake value for selected fields
   * Returns undefined if no valid data is available
   * Uses only fields with valid scan data for the calculation
   * @param data Array of planning wizard selection grid data
   * @param operationModeEnum Definition of operation mode
   * @returns Calculated area-weighted N-uptake average or undefined
   * @private
   */
  private calculateNUptakeAreaWightedAverage(data: IPpPlanningWizardSelectionGridData[],
                                             operationModeEnum: OperationModesEnum): number | undefined {
    if (operationModeEnum === OperationModesEnum.Constant || !data || data.length === 0) {
      return undefined;
    }
    return this.nFertilizationPlantProtectionService.calculateAreaWeightedAverageOfScans(data);
  }

  /**
   * Generates a tooltip displaying the ingredients of a product
   * Parses the ingredients from the product's JSON-encoded ingredients list
   * Creates a grid-style tooltip with each ingredient on a separate row
   * @param dataItem The product list grid item containing product information
   * @returns An HTML string representing the ingredients' tooltip, or undefined if no ingredients are available
   * @private
   */
  private generateProductNameTooltip(dataItem: IPpProductListGridItem): string | undefined {
    if (!dataItem?.ProductListItem?.Product?.Ingredients) {
      return undefined;
    }
    const parsedIngredients = JSON.parse(dataItem.ProductListItem.Product.Ingredients) as string[];
    if (!Array.isArray(parsedIngredients) || parsedIngredients.length === 0) {
      return undefined;
    }
    let fieldsTooltip = '<div style="display: grid; grid-template-rows: auto auto;">';
    parsedIngredients.forEach((ingredient, index) => {
      fieldsTooltip += `<div style="grid-row: ${index + 1}">${ingredient}</div>`;
    });
    fieldsTooltip += '</div>';
    return fieldsTooltip;
  }
}
